import { createMemo, type Component } from 'solid-js';
import { NA, formatCurrency, formatNumber, formatPercent } from '~/lib/format';
import { Card } from '~/features/cards';
import { exposures, identified, overlapping, overlappingValue, resolvedValue, type Axis } from '~/stores/exposure-store';

// The three figures the table cannot state, because a table states rows and
// these are properties of the whole.
//
// Three follow the tab, since "the largest thing you own" is a different
// question per axis. Held more than once does not: a sector reached through two
// funds is not a finding — that is what sectors are — and only a company
// appearing twice means you own it twice without having decided to.
//
// No coverage figure here. What has and has not been classified is a fact about
// the data behind the product rather than about the book, and it belongs in the
// pipeline's own checks — the red note in the toolbar already says when a fund
// could not be resolved.

const TOP_N = 5;

const ExposureCards: Component<{ axis: Axis }> = (props) => {
    const rows = createMemo(() => exposures(props.axis));

    const largest = createMemo(() => rows()[0] ?? null);

    // Concentration in one number. The five biggest against everything resolved,
    // which is the same denominator every share on the page uses.
    const topShare = createMemo(() => {
        const total = resolvedValue(props.axis);
        if (total <= 0) return null;
        return (rows().slice(0, TOP_N).reduce((sum, e) => sum + e.value, 0) / total) * 100;
    });

    const noun = () => (props.axis === 'company' ? 'company' : props.axis === 'sector' ? 'sector' : 'region');
    const plural = () => (props.axis === 'company' ? 'companies' : props.axis === 'sector' ? 'sectors' : 'regions');

    return (
        <div class='flex gap-px border-b bg-border'>
            <div class='min-w-0 flex-1'>
                <Card label={`Largest ${noun()}`} value={largest() ? formatPercent(largest()!.share, 1) : NA}>
                    {largest()?.name ?? 'nothing resolved'}
                </Card>
            </div>
            <div class='min-w-0 flex-1'>
                {/* Red past half: at that point five names are most of the book,
                    whatever the fund count suggests. */}
                <Card label={`Top ${TOP_N}`} value={formatPercent(topShare(), 1)} tone={topShare() != null && topShare()! >= 50 ? 'red' : undefined}>
                    of {formatNumber(rows().length)} {plural()}
                </Card>
            </div>
            <div class='min-w-0 flex-1'>
                {/* Always companies, whatever tab is showing. */}
                <Card label='Held more than once' value={formatNumber(overlapping().length)} tone={overlapping().length > 0 ? 'red' : undefined}>
                    of {formatNumber(identified().length)} companies · {formatCurrency(overlappingValue())}
                </Card>
            </div>
        </div>
    );
};

export default ExposureCards;
